import React from "react";
import {
  Modal as RNModal,
  Pressable,
  StyleSheet,
  View,
  ViewStyle
} from "react-native";
import { colors, layout, shadows } from "@/constants";
import HeaderButton from "../HeaderButton";
import Text from "./Text";

/**
 * Width preset for the modal card.
 *
 * Sizes come from `layout.modal` so the modal scales with the same constants
 * the rest of the app uses; no width is hardcoded in this component.
 */
export type ModalSize = "dialog" | "medium" | "wide";

interface ModalProps {
  /** Whether the modal is visible */
  visible: boolean;
  /** Width preset. Defaults to "dialog". */
  size?: ModalSize;
  /** Optional title rendered at the top of the card */
  title?: string;
  /** Horizontal alignment of the title. Defaults to "left". */
  titleAlign?: "left" | "center";
  /**
   * Optional close handler. When omitted the modal is non-dismissable —
   * tapping the scrim and hardware back do nothing, and no header close
   * button is rendered.
   */
  onRequestClose?: () => void;
  /** Show an "x" close button in the header. Requires `onRequestClose`. */
  showCloseButton?: boolean;
  /** Whether tapping the scrim outside the card invokes `onRequestClose`. */
  dismissOnBackdropPress?: boolean;
  /** Modal body */
  children: React.ReactNode;
  /**
   * Optional footer row. Laid out as `flex-direction: row` with
   * `justifyContent: space-between` — a `[Cancel, Primary]` pair lands at
   * opposite ends. A single child stays on the left; callers that want it
   * centered should wrap it themselves.
   */
  footer?: React.ReactNode;
  /** Optional override for the card style */
  cardStyle?: ViewStyle;
}

const SIZE_WIDTHS: Record<ModalSize, number> = {
  dialog: layout.modal.dialog,
  medium: layout.modal.medium,
  wide: layout.modal.wide
};

/**
 * Scrim + centered card. The single primitive every app dialog renders
 * inside (delete confirm, feedback, crisis resources).
 *
 * The scrim is only a `Pressable` when `dismissOnBackdropPress` is true —
 * otherwise it's a plain `View`, which avoids the desktop hand-cursor
 * suggesting the entire backdrop is clickable.
 */
export default function Modal({
  visible,
  size = "dialog",
  title,
  titleAlign = "left",
  onRequestClose,
  showCloseButton = false,
  dismissOnBackdropPress = true,
  children,
  footer,
  cardStyle
}: ModalProps) {
  const backdropDismissable =
    dismissOnBackdropPress && onRequestClose !== undefined;

  // Apply width + maxWidth directly on the card element so the card sizes
  // against the scrim regardless of which scrim variant wraps it. Wrapping
  // the card in an unsized Pressable collapsed `width: "100%"` to content
  // width on web.
  const cardStyleResolved = [
    styles.card,
    { maxWidth: SIZE_WIDTHS[size] },
    cardStyle
  ];
  const cardContent = (
    <>
      {(title || (showCloseButton && onRequestClose)) && (
        <View style={styles.header}>
          {title ? (
            <Text
              variant="semiBold"
              size="large"
              style={[
                styles.title,
                titleAlign === "center" && styles.titleCenter
              ]}
            >
              {title}
            </Text>
          ) : (
            <View style={styles.title} />
          )}
          {showCloseButton && onRequestClose ? (
            <HeaderButton
              icon="close"
              onPress={onRequestClose}
              accessibilityLabel="Close"
            />
          ) : null}
        </View>
      )}
      <View style={styles.body}>{children}</View>
      {footer ? <View style={styles.footer}>{footer}</View> : null}
    </>
  );

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onRequestClose}
    >
      {backdropDismissable ? (
        <Pressable style={styles.scrim} onPress={onRequestClose}>
          {/* Swallow taps inside the card so they don't reach the scrim. */}
          <Pressable style={cardStyleResolved} onPress={() => {}}>
            {cardContent}
          </Pressable>
        </Pressable>
      ) : (
        <View style={styles.scrim}>
          <View style={cardStyleResolved}>{cardContent}</View>
        </View>
      )}
    </RNModal>
  );
}

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    backgroundColor: colors.scrim,
    justifyContent: "center",
    alignItems: "center",
    padding: layout.space.lg
  },
  card: {
    width: "100%",
    // Never exceed the viewport (scrim is full-screen, padded). A body that
    // overflows shrinks and scrolls rather than pushing the header/footer
    // off-screen.
    maxHeight: "100%",
    backgroundColor: colors.surface,
    borderRadius: layout.borderRadius.large,
    ...shadows.raised,
    overflow: "hidden"
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: layout.space.lg,
    paddingTop: layout.space.lg
  },
  title: {
    flex: 1
  },
  titleCenter: {
    textAlign: "center"
  },
  body: {
    // Allow the body to shrink within a height-capped card so a scrollable
    // child (e.g. the feedback form) scrolls instead of overflowing.
    flexShrink: 1,
    padding: layout.space.lg
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: layout.space.sm,
    paddingHorizontal: layout.space.lg,
    paddingBottom: layout.space.lg
  }
});
